import type { ReactNode } from "react";

import { autocompleteStates } from "app/components/compare/WebsiteQueryBarTypes";

import { NoData } from "../../components/NoData";

import type { IGetListItemsGroupsParams, MinimalListItemData, SupportedAutocompleteStates } from "./getListItemsGroups";
import { getListItemsGroups, isAutocompleteState } from "./getListItemsGroups";
import type { GroupsRenderOrder, SupportedGroups } from "./getOrderedGroupNames";
import { getOrderedGroupNames } from "./getOrderedGroupNames";

const renderDefaultNoSimilarsitesData = () => (
    <NoData
        key="no-similar-data"
        text="autocomplete.websitesCompare.similarsites.nodata"
    />
);
const renderDefaultNoSearchData = () => (
    <NoData
        key="no-search-data"
        text="autocomplete.websitesCompare.searches.nodata"
    />
);
const renderDefaultNoDataError = () => (
    <NoData
        key="search-error"
        text="autocomplete.websitesCompare.error.nodata"
    />
);

interface IGetListItemsGroupsContentParams<T> extends IGetListItemsGroupsParams<T> {
    groupsRenderOrder?: GroupsRenderOrder;
    renderListItem: (item: T, index: number) => ReactNode;
    renderNoDataError?: () => ReactNode;
    renderNoSearchData?: () => ReactNode;
    renderNoSimilarsitesData?: () => ReactNode;
}

export const getListItemsGroupsContent = <T extends MinimalListItemData>({
    listItems,
    groupsRenderOrder,
    renderListItem,
    renderNoDataError = renderDefaultNoDataError,
    renderNoSearchData = renderDefaultNoSearchData,
    renderNoSimilarsitesData = renderDefaultNoSimilarsitesData,
}: IGetListItemsGroupsContentParams<T>) => {
    const groups = getListItemsGroups({ listItems });

    const groupsContent: Record<SupportedGroups, Array<ReactNode>> = {
        recents: [],
        similar: [],
        searchResults: [],
    };

    const renderItem = (item: T | SupportedAutocompleteStates, index: number) => {
        if (isAutocompleteState(item)) {
            switch (item) {
                case autocompleteStates.NO_DATA_SIMILARSITES: {
                    return renderNoSimilarsitesData();
                }
                case autocompleteStates.NO_DATA_SEARCHES: {
                    return renderNoSearchData();
                }
                case autocompleteStates.ERROR:
                    return renderNoDataError();
                default:
                    return null;
            }
        }

        return renderListItem(item, index);
    };

    const orderedGroupNames = getOrderedGroupNames(groupsRenderOrder);

    let renderIndex = -1;

    orderedGroupNames.forEach((groupName) => {
        groups[groupName].forEach((item) => {
            renderIndex++;
            groupsContent[groupName].push(renderItem(item, renderIndex));
        });
    });

    return groupsContent;
};
